import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useGoogleOneTapLogin } from '@react-oauth/google';
import { useAuth } from '../contexts/AuthContext';
import { purgeAuthState } from '../contexts/AuthContext';
import { useTranslation } from '../contexts/TranslationContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { ArrowRight, Mail, Loader2, RotateCcw } from 'lucide-react';
import axios from 'axios';
import { resolveLandingPath } from '../lib/landingPath';

import { API_BASE } from '../lib/apiBase';
const API = `${API_BASE}/api`;

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const GOOGLE_ONE_TAP_ENABLED = Boolean(process.env.REACT_APP_GOOGLE_ONE_TAP_CLIENT_ID);

const DEFAULT_BG = 'https://images.unsplash.com/photo-1583927109257-f21c74dd0c3f?crop=entropy&cs=srgb&fm=jpg&q=85';

export default function LoginPage() {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [magicLinkEmail, setMagicLinkEmail] = useState('');
    const [magicLinkSent, setMagicLinkSent] = useState(false);
    const [magicLinkLoading, setMagicLinkLoading] = useState(false);
    const [showMagicLink, setShowMagicLink] = useState(false);
    const [sessionReset, setSessionReset] = useState(false);
    const [bgImages, setBgImages] = useState([]);
    const [configLoaded, setConfigLoaded] = useState(false);
    const [slideEnabled, setSlideEnabled] = useState(false);
    const [slideInterval, setSlideInterval] = useState(6);
    const [activeBgIdx, setActiveBgIdx] = useState(0);
    const { login, loginWithGoogleOneTap } = useAuth();
    const { t } = useTranslation();
    const navigate = useNavigate();

    // Google One Tap — only enabled if a Client ID is configured
    useGoogleOneTapLogin({
        disabled: !GOOGLE_ONE_TAP_ENABLED || Boolean(localStorage.getItem('token')),
        onSuccess: async (credentialResponse) => {
            const credential = credentialResponse?.credential;
            if (!credential) return;
            try {
                const userData = await loginWithGoogleOneTap(credential);
                toast.success(`Welcome back, ${userData.name || userData.email}`);
                navigate(resolveLandingPath(userData), { replace: true });
            } catch (err) {
                toast.error(err.response?.data?.detail || 'Google sign-in failed');
            }
        },
        onError: () => {
            // Silently ignore — user can still use email/password
        },
        cancel_on_tap_outside: false,
        use_fedcm_for_prompt: true,
    });

    // Fetch login-page config (custom images + slideshow settings)
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const res = await axios.get(`${API}/auth/login-page-config?page=login`);
                if (cancelled) return;
                const imgs = (res.data?.images || []).map((i) => `${API_BASE}${i.url}`);
                setBgImages(imgs.length > 0 ? imgs : [DEFAULT_BG]);
                setSlideEnabled(Boolean(res.data?.settings?.slideshow_enabled) && imgs.length > 1);
                setSlideInterval(Math.max(2, Math.min(60, Number(res.data?.settings?.slideshow_interval_seconds) || 6)));
            } catch {
                if (!cancelled) setBgImages([DEFAULT_BG]);
            } finally {
                if (!cancelled) setConfigLoaded(true);
            }
        })();
        return () => { cancelled = true; };
    }, []);

    // Slideshow rotation
    useEffect(() => {
        if (!slideEnabled || bgImages.length < 2) return;
        const t = setInterval(() => {
            setActiveBgIdx((idx) => (idx + 1) % bgImages.length);
        }, slideInterval * 1000);
        return () => clearInterval(t);
    }, [slideEnabled, slideInterval, bgImages.length]);

    // Compute the landing page after login using the shared helper — same
    // helper PublicRoute uses so both paths agree on the destination.
    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsLoading(true);

        try {
            const user = await login(email, password);
            toast.success(`Welcome back, ${user.name}!`);
            navigate(resolveLandingPath(user));
        } catch (error) {
            const message = error.response?.data?.detail || 'Login failed';
            toast.error(message);
        } finally {
            setIsLoading(false);
        }
    };

    const handleMagicLink = async (e) => {
        e.preventDefault();
        if (!magicLinkEmail.trim()) return;
        setMagicLinkLoading(true);
        try {
            await axios.post(`${API}/auth/magic-link/request`, { email: magicLinkEmail.trim() });
            setMagicLinkSent(true);
            toast.success('Login link sent! Check your inbox.');
        } catch {
            toast.error('Failed to send login link. Try again.');
        } finally {
            setMagicLinkLoading(false);
        }
    };

    return (
        <div className="min-h-screen lg:h-screen lg:overflow-hidden flex" data-testid="login-page">
            {/* Left Side - Form */}
            <div className="w-full lg:w-1/2 flex flex-col px-8 py-5 bg-background lg:overflow-y-auto">
                {/* Logo - Top Left. Small fixed gap below the logo so it
                    reads as a separate visual layer from the sign-in
                    card. On taller viewports `flex-1` further floats the
                    card toward the middle of the remaining space. */}
                <div className="flex items-center mb-4 lg:mb-6">
                    <img src="/owntune-logo-dark.png" alt="Owntune" className="h-8 w-auto" />
                </div>

                {/* Form — viewport-centred via flex. With `lg:h-screen`
                    on the parent the whole card is guaranteed to fit on
                    1366×768 laptops without scrolling. */}
                <div className="flex-1 flex items-center justify-center min-h-0">
                    <div className="w-full max-w-md animate-fadeIn">
                        <Card className="border-0 shadow-premium-lg">
                        <CardHeader className="space-y-0.5 pb-3">
                            <CardTitle className="text-2xl font-bold font-display">
                                {t('auth.signin', 'Sign in')}
                            </CardTitle>
                            <CardDescription>
                                {t('auth.signin.subtitle', 'Enter your credentials to continue')}
                            </CardDescription>
                        </CardHeader>
                        <CardContent>
                            <form onSubmit={handleSubmit} className="space-y-3">
                                <div className="space-y-2">
                                    <Label htmlFor="email">{t('auth.email', 'Email')}</Label>
                                    <Input
                                        id="email"
                                        type="email"
                                        placeholder="name@company.com"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        required
                                        className="h-11"
                                        data-testid="login-email-input"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <div className="flex items-center justify-between">
                                        <Label htmlFor="password">{t('auth.password', 'Password')}</Label>
                                        <Link 
                                            to="/forgot-password" 
                                            className="text-xs text-purple-600 hover:text-purple-700 font-medium"
                                            data-testid="forgot-password-link"
                                        >
                                            {t('auth.forgot_password', 'Forgot password?')}
                                        </Link>
                                    </div>
                                    <Input
                                        id="password"
                                        type="password"
                                        placeholder="••••••••"
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        required
                                        className="h-11"
                                        data-testid="login-password-input"
                                    />
                                </div>
                                <Button 
                                    type="submit" 
                                    className="w-full h-11 owntune-cta btn-active transition-all"
                                    disabled={isLoading}
                                    data-testid="login-submit-button"
                                >
                                    {isLoading ? (
                                        <span className="flex items-center gap-2">
                                            <div className="spinner w-4 h-4 border-2"></div>
                                            {t('common.loading', 'Loading...')}
                                        </span>
                                    ) : (
                                        <span className="flex items-center gap-2">
                                            {t('auth.signin', 'Sign in')}
                                            <ArrowRight className="h-4 w-4" />
                                        </span>
                                    )}
                                </Button>
                            </form>

                            {/* OR divider */}
                            <div className="relative my-6">
                                <div className="absolute inset-0 flex items-center">
                                    <span className="w-full border-t border-slate-200" />
                                </div>
                                <div className="relative flex justify-center text-xs uppercase">
                                    <span className="bg-white px-3 text-slate-400 font-medium">OR</span>
                                </div>
                            </div>

                            {/* Continue with Google */}
                            <Button
                                type="button"
                                variant="outline"
                                className="w-full h-11 font-medium border-slate-300 hover:bg-slate-50 text-slate-700"
                                data-testid="google-login-button"
                                onClick={() => {
                                    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
                                    const redirectUrl = window.location.origin;
                                    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
                                }}
                            >
                                <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24">
                                    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
                                    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                                    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                                    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                                </svg>
                                {t('auth.google', 'Continue with Google')}
                            </Button>

                            {/* Magic Link */}
                            {!showMagicLink ? (
                                <Button
                                    type="button"
                                    variant="outline"
                                    className="w-full h-11 font-medium border-slate-300 hover:bg-slate-50 text-slate-700 mt-3"
                                    data-testid="magic-link-toggle-button"
                                    onClick={() => setShowMagicLink(true)}
                                >
                                    <Mail className="mr-2 h-4 w-4" />
                                    {t('auth.magic_link', 'Sign in with email link')}
                                </Button>
                            ) : magicLinkSent ? (
                                <div className="mt-3 p-4 bg-green-50 border border-green-200 rounded-lg text-center" data-testid="magic-link-sent">
                                    <Mail className="h-6 w-6 text-green-600 mx-auto mb-2" />
                                    <p className="text-sm font-medium text-green-800">Check your inbox!</p>
                                    <p className="text-xs text-green-600 mt-1">We sent a login link to <strong>{magicLinkEmail}</strong></p>
                                    <button 
                                        className="text-xs text-blue-600 hover:underline mt-2"
                                        onClick={() => { setMagicLinkSent(false); setShowMagicLink(false); }}
                                    >
                                        Use a different method
                                    </button>
                                </div>
                            ) : (
                                <form onSubmit={handleMagicLink} className="mt-3 space-y-2" data-testid="magic-link-form">
                                    <Input
                                        type="email"
                                        placeholder="your@email.com"
                                        value={magicLinkEmail}
                                        onChange={(e) => setMagicLinkEmail(e.target.value)}
                                        required
                                        className="h-10"
                                        data-testid="magic-link-email-input"
                                        autoFocus
                                    />
                                    <div className="flex gap-2">
                                        <Button
                                            type="submit"
                                            className="flex-1 h-9 bg-slate-900 hover:bg-slate-800 text-white text-sm"
                                            disabled={magicLinkLoading}
                                            data-testid="magic-link-submit-button"
                                        >
                                            {magicLinkLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Send login link'}
                                        </Button>
                                        <Button
                                            type="button"
                                            variant="ghost"
                                            className="h-9 text-sm px-3"
                                            onClick={() => setShowMagicLink(false)}
                                        >
                                            Cancel
                                        </Button>
                                    </div>
                                </form>
                            )}

                            <div className="mt-6 text-center">
                                <p className="text-sm text-muted-foreground">
                                    {t('auth.signup.prompt', "Don't have an account?")}{' '}
                                    <Link 
                                        to="/register" 
                                        className="text-indigo-600 hover:text-indigo-700 font-medium"
                                        data-testid="register-link"
                                    >
                                        {t('auth.register', 'Create account')}
                                    </Link>
                                </p>
                            </div>

                            {/* Reset Session Fallback */}
                            <div className="mt-4 pt-4 border-t border-slate-100 text-center">
                                {sessionReset ? (
                                    <p className="text-xs text-emerald-600 font-medium" data-testid="session-reset-done">
                                        Session cleared. You can now log in fresh.
                                    </p>
                                ) : (
                                    <button
                                        type="button"
                                        onClick={() => {
                                            purgeAuthState('LoginPage:Reset-Session-button');
                                            // Also clear any other app-specific cache
                                            try {
                                                Object.keys(localStorage).forEach((key) => {
                                                    if (key.startsWith('owntune_') || key === 'token' || key === 'user') {
                                                        localStorage.removeItem(key);
                                                    }
                                                });
                                            } catch { /* ignore */ }
                                            setSessionReset(true);
                                            toast.success('Login session reset. Try logging in again.');
                                        }}
                                        className="text-xs text-slate-400 hover:text-slate-600 transition-colors flex items-center justify-center gap-1 mx-auto"
                                        data-testid="reset-session-button"
                                    >
                                        <RotateCcw className="h-3 w-3" />
                                        Having trouble? Reset login session
                                    </button>
                                )}
                            </div>
                        </CardContent>
                    </Card>
                    </div>
                </div>
            </div>

            {/* Right Side - Image (with optional slideshow) */}
            <div className="hidden lg:block lg:w-1/2 login-image relative overflow-hidden bg-background" data-testid="login-hero-image">
                {configLoaded && bgImages.map((src, idx) => (
                    <div
                        key={src + idx}
                        className="absolute inset-0 bg-cover bg-center transition-opacity duration-[1200ms] ease-in-out"
                        style={{
                            backgroundImage: `url(${src})`,
                            opacity: idx === activeBgIdx ? 1 : 0,
                        }}
                    />
                ))}
            </div>
        </div>
    );
}
